import { useState, useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import apiClient, { proactiveRefresh, startTokenAutoRefresh, stopTokenAutoRefresh } from "@/api/client";
import type { AuthUser } from "@/store/auth";
import { usePresence } from "@/hooks/usePresence";
import { usePushNavigation } from "@/hooks/usePushNavigation";
import { RedirectToLogin } from "@/routes/RedirectToLogin";
import { MaintenanceBanner } from "@/features/maintenance/components/MaintenanceBanner";
import { MaintenanceActiveBar } from "@/features/maintenance/components/MaintenanceActiveBar";
import { MaintenanceDeploymentActiveBanner } from "@/features/maintenance/components/MaintenanceDeploymentActiveBanner";
import { MaintenanceEndedModal } from "@/features/maintenance/components/MaintenanceEndedModal";
import { useMaintenanceStatus } from "@/features/maintenance/hooks/useMaintenanceStatus";
import type { MaintenanceWindow } from "@/features/maintenance/types";
import { PageTitle } from "@/components/PageTitle";
import { Spinner } from "@/components/ui/Spinner";
import { ClearSessionButton } from "@/components/ClearSessionButton";
import { AronDockHost } from "@/features/aron/components/AronDockHost";
import { AronApprovalModalHost } from "@/features/aron/components/AronApprovalModal";
import { AronFloatHost } from "@/features/aron/components/AronFloatHost";
import { ScreenShareHost } from "@/features/screenshare/components/ScreenShareHost";
import { MeetingInvitesHost } from "@/features/meetings/components/IncomingMeetingToast";
import { MeetingRoomProvider } from "@/features/meetings/components/MeetingRoomProvider";
import { MeetingDockAside } from "@/features/meetings/components/MeetingDockAside";
import { LoginChangesModalHost } from "@/features/whats_new/components/LoginChangesModal";
import { SystemMessagePopupHost } from "@/features/system_messages/components/SystemMessagePopupHost";
import { TimesheetLockBanner } from "@/features/timesheets/components/TimesheetLockBanner";
import { useTimesheetLock } from "@/features/timesheets/hooks/useTimesheets";

// Sections that stay reachable while the missing-timesheet lock is on: the two
// the lock leaves in the nav (Urlopy + Harmonogram) plus the account settings
// the avatar in the TopBar points at. Everything else redirects to /timesheets.
const TIMESHEET_LOCK_ALLOWED_PATHS = [
  "/timesheets",
  "/leaves",
  "/sick-leaves",
  "/schedule",
  "/settings",
];

export function isAllowedWhileTimesheetLocked(pathname: string): boolean {
  return TIMESHEET_LOCK_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function ProtectedLayout() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const [showEndedModal, setShowEndedModal] = useState(false);
  const [endedWasDeployment, setEndedWasDeployment] = useState(false);

  usePresence();
  usePushNavigation();

  // Proactively refresh access token when the user returns to this tab so the
  // token is already fresh before any API request fires.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") proactiveRefresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Keep the access token fresh even in a tab that stays focused all day (never
  // fires `visibilitychange`): schedule a refresh ~60 s before expiry. Re-runs on
  // token rotation so it always schedules off the newest token; stops on logout/unmount.
  useEffect(() => {
    startTokenAutoRefresh();
    return () => stopTokenAutoRefresh();
  }, [accessToken]);

  useEffect(() => {
    // No credentials at all: this render is already returning <RedirectToLogin />
    // below, but effects run regardless of what a component rendered. Firing the
    // profile fetch anyway sent an anonymous request that could only come back 401,
    // and that 401 used to end up in the forced-logout path — so simply opening the
    // app while signed out hard-reloaded the login screen a second after it appeared.
    if (!accessToken && !refreshToken) return;
    let cancelled = false;
    apiClient
      .get<{ data: AuthUser }>("/auth/me/")
      .then((r) => { if (!cancelled) updateUser(r.data.data); })
      .catch(() => {/* token invalid — logout handled by axios interceptor */});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: maintenanceStatus } = useMaintenanceStatus();
  // Employee who never filed last month's card: everything but Urlopy +
  // Harmonogram is off until they do (no approval needed — see
  // apps/timesheets/services/lockout.py).
  const { isLocked: timesheetLocked, periodLabel: timesheetLockPeriod } =
    useTimesheetLock();

  // Detect active → ended transition to show the reload modal
  const prevActiveRef = useRef<MaintenanceWindow | null | undefined>(undefined);
  useEffect(() => {
    if (maintenanceStatus === undefined) return;
    const prev = prevActiveRef.current;
    const current = maintenanceStatus.active ?? null;
    if (prev !== undefined && prev !== null && current === null) {
      setEndedWasDeployment(prev.is_deployment);
      setShowEndedModal(true);
    }
    prevActiveRef.current = current;
  }, [maintenanceStatus]);

  // Genuinely logged out — no credentials at all → go to the login page, keeping
  // the destination so a deep link (e.g. a GG notification) resumes after login.
  if (!accessToken && !refreshToken) {
    return <RedirectToLogin />;
  }

  // Credentials present but the profile hasn't loaded yet. This happens after a
  // full page reload (e.g. the post-login cache reload) on devices where `user`
  // was not restored from storage even though the tokens were: the access token
  // is valid but `user` is null. Wait for the /auth/me/ effect above to populate
  // it instead of bouncing to the login page — bouncing here logged users
  // straight back out immediately after a successful login.
  if (!user) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 bg-[var(--color-bg-base)]">
        <Spinner size="lg" />
        {/* Escape hatch: if the profile never loads (e.g. a stale token), the
            user would otherwise be stuck here forever with no way out. */}
        <ClearSessionButton />
      </div>
    );
  }

  const active = maintenanceStatus?.active;
  // Manual windows fully block non-devs (redirect to /maintenance).
  // Deployment windows only show a warning banner — the site keeps working,
  // brief blips during the ECS rolling restart are tolerated.
  if (active && !active.is_deployment && !user.is_developer) {
    return <Navigate to="/maintenance" replace />;
  }

  if (timesheetLocked && !isAllowedWhileTimesheetLocked(pathname)) {
    return <Navigate to="/timesheets" replace />;
  }

  return (
    <MeetingRoomProvider>
    <div className="flex flex-col h-[100dvh] overflow-hidden bg-[var(--color-bg-base)]">
      <PageTitle />
      {active && user.is_developer && <MaintenanceActiveBar active={active} />}
      {active && active.is_deployment && !user.is_developer && (
        <MaintenanceDeploymentActiveBanner active={active} />
      )}
      {maintenanceStatus?.upcoming && (
        <MaintenanceBanner upcoming={maintenanceStatus.upcoming} />
      )}
      {timesheetLocked && (
        <TimesheetLockBanner periodLabel={timesheetLockPeriod} />
      )}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex flex-col flex-1 overflow-hidden min-w-0">
          <TopBar onMenuClick={() => setSidebarOpen((o) => !o)} />
          <main className="flex-1 overflow-y-auto overflow-x-auto p-4 lg:p-6">
            <Outlet />
          </main>
        </div>
        <AronDockHost />
        <MeetingDockAside />
      </div>
      <MaintenanceEndedModal open={showEndedModal} wasDeployment={endedWasDeployment} onCancel={() => setShowEndedModal(false)} />
      <AronFloatHost />
      <AronApprovalModalHost />
      <LoginChangesModalHost />
      <SystemMessagePopupHost />
      <ScreenShareHost />
      <MeetingInvitesHost />
    </div>
    </MeetingRoomProvider>
  );
}
